import { AnalyticsTableRowTypeChannels } from '@/features/analytics/types'
import { MagnifyingGlassSVGIcon } from '@/shared/assets'
import { SortHeader, StaticHeader } from '@/shared/components'
import { GreyRubleIconInline } from '@/shared/ui'
import { type UseSearch, type UseSortedData } from '@/features/analytics/hooks'

import { DropdownMenuDemo } from '../DropdownMenu'

import {
    TABLE_COLUMNS_LAYOUT,
    type TableReactNodeRecord,
} from './tableColumnsLayout'
import { Channel } from '@/true-fsd/data/advertiser-account'

export type TableHeadRowProps = {
    groupId: number
    activeChannel: Channel
} & Pick<
    UseSearch<AnalyticsTableRowTypeChannels>,
    'setSearchQuery' | 'searchQuery'
> &
    Pick<
        UseSortedData<AnalyticsTableRowTypeChannels>,
        'sortConfig' | 'handleSort'
    >

export const TableHeadRow = ({
    groupId,
    activeChannel,
    searchQuery,
    setSearchQuery,
    handleSort,
    sortConfig,
}: TableHeadRowProps) => {
    const tableHead: TableReactNodeRecord = {
        marking: <StaticHeader title="Маркировка" alignment="right" />,

        select: (
            <div className="flex h-full w-full items-center justify-center">
                <DropdownMenuDemo
                    groupId={groupId}
                    channelId={activeChannel.channel_id}
                />
            </div>
        ),
        name: (
            <div className="search-header w-full !gap-0">
                <div className="search-input-wrapper">
                    <MagnifyingGlassSVGIcon className="search-header__icon" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Название"
                        className="search-input"
                    />
                </div>
                <SortHeader<AnalyticsTableRowTypeChannels>
                    title=""
                    field="name"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </div>
        ),
        status: <StaticHeader title="Статус" alignment="right" />,
        link: <StaticHeader title="Ссылка" alignment="right" />,
        creationDate: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Дата"
                field="creationDate"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        creative: <StaticHeader title="Креатив" alignment="right" />,
        publishedAt: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Дата выхода"
                field="publishedAt"
                sortConfig={sortConfig}
                onSort={handleSort}
                className="sort-header__flex-end"
                classNameSpan="sort-header__span__max-width"
            />
        ),
        publishingDeletionDate: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Дата Удаления"
                field="publishingDeletionDate"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        subscribers: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Кол-во пдп"
                field="subscribers"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        averageReach: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Средний охват постов"
                field="averageReach"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        err: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="% ERR"
                field="err"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        CPMForcasted: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title={
                    <>
                        Прогноз CPM <GreyRubleIconInline />
                    </>
                }
                field="CPMForcasted"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        reach: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Охват поста"
                field="reach"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        subscribed: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Вступлений"
                field="subscribed"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        subscribedPerDay: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Вступлений за 24 часа"
                field="subscribedPerDay"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        CTR: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="CR %"
                field="CTR"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        CPM: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title={
                    <>
                        CPM <GreyRubleIconInline />
                    </>
                }
                field="CPM"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        CPS: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title={
                    <>
                        CPS <GreyRubleIconInline />
                    </>
                }
                field="CPS"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        unsubscribed: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Отписки"
                field="unsubscribed"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        unsubscriptionPercentage: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Отписки, %"
                field="unsubscriptionPercentage"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        totalSubscribed: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title="Итого вступлений"
                field="totalSubscribed"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        total_CPS: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title={
                    <>
                        Итого CPS <GreyRubleIconInline />
                    </>
                }
                field="total_CPS"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
        budget: (
            <SortHeader<AnalyticsTableRowTypeChannels>
                title={
                    <>
                        Бюджет <GreyRubleIconInline />
                    </>
                }
                field="budget"
                sortConfig={sortConfig}
                onSort={handleSort}
            />
        ),
    }

    return (
        <tr>
            {TABLE_COLUMNS_LAYOUT.map(({ key, style, className }) => (
                <th key={key} style={style} className={className}>
                    {tableHead[key]}
                </th>
            ))}
        </tr>
    )
}
